// src/pages/Admin/UserAdmin/EmployeeHierarchyChangesPage.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Container, Form, InputGroup, Pagination, Spinner, Table } from 'react-bootstrap';
import { FaExternalLinkAlt, FaHistory, FaSearch, FaSort, FaSortDown, FaSortUp, FaUserTie } from 'react-icons/fa';
import {
  EmployeeHierarchyChange,
  EmployeeHierarchyChangeSuggestion,
  getEmployeeHierarchyChanges,
  PagedResult,
  searchEmployeeHierarchyChanges,
} from '@/api/userAdminApi';
import './SpAccountPurgePage.scss';

const PAGE_SIZE = 25;
type SortDirection = 'asc' | 'desc';

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : '-');
const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : '-');

function PaginationBar({
  page, pageSize, total, onChange,
}: {
  page: number; pageSize: number; total: number; onChange: (p: number) => void;
}) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <small className="text-muted">
        {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
      </small>
      <Pagination size="sm" className="mb-0">
        <Pagination.Prev disabled={page === 1} onClick={() => onChange(page - 1)} />
        {page > 2 && <Pagination.Item onClick={() => onChange(1)}>1</Pagination.Item>}
        {page > 3 && <Pagination.Ellipsis />}
        {page > 1 && <Pagination.Item onClick={() => onChange(page - 1)}>{page - 1}</Pagination.Item>}
        <Pagination.Item active>{page}</Pagination.Item>
        {page < totalPages && <Pagination.Item onClick={() => onChange(page + 1)}>{page + 1}</Pagination.Item>}
        {page < totalPages - 2 && <Pagination.Ellipsis />}
        {page < totalPages - 1 && <Pagination.Item onClick={() => onChange(totalPages)}>{totalPages}</Pagination.Item>}
        <Pagination.Next disabled={page === totalPages} onClick={() => onChange(page + 1)} />
      </Pagination>
    </div>
  );
}

function SortHeader({
  label, column, sortBy, sortDirection, onSort,
}: {
  label: string;
  column: string;
  sortBy: string;
  sortDirection: SortDirection;
  onSort: (column: string) => void;
}) {
  const active = sortBy === column;
  return (
    <button type="button" className="spa-sort-header" onClick={() => onSort(column)}>
      <span>{label}</span>
      {active ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
    </button>
  );
}

const EmployeeHierarchyChangesPage: React.FC = () => {
  const [emailInput, setEmailInput] = useState('');
  const [selectedEmail, setSelectedEmail] = useState('');
  const [suggestions, setSuggestions] = useState<EmployeeHierarchyChangeSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const [data, setData] = useState<PagedResult<EmployeeHierarchyChange> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('loadDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    if (!selectedEmail.trim()) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getEmployeeHierarchyChanges(selectedEmail.trim(), page, PAGE_SIZE, sortBy, sortDirection));
    } catch {
      setError('Failed to load employee hierarchy change history');
    } finally {
      setLoading(false);
    }
  }, [page, selectedEmail, sortBy, sortDirection]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = emailInput.trim();
    if (term.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    let cancelled = false;
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchEmployeeHierarchyChanges(term);
        if (!cancelled) {
          setSuggestions(results);
          setShowSuggestions(results.length > 0);
        }
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);

    return () => { cancelled = true; };
  }, [emailInput]);

  function submit(e?: React.FormEvent) {
    e?.preventDefault();
    const email = emailInput.trim();
    if (!email) return;
    setSelectedEmail(email);
    setPage(1);
    setData(null);
    setShowSuggestions(false);
  }

  function selectSuggestion(suggestion: EmployeeHierarchyChangeSuggestion) {
    setEmailInput(suggestion.employeeEmail);
    setSelectedEmail(suggestion.employeeEmail);
    setPage(1);
    setData(null);
    setShowSuggestions(false);
  }

  function sort(column: string) {
    setPage(1);
    if (sortBy === column) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection(column === 'loadDate' ? 'desc' : 'asc');
    }
  }

  function openUser(identifier?: string) {
    if (!identifier) return;
    window.open(`/admin/users/details/${encodeURIComponent(identifier)}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <Container className="mt-4 spa-page">
      <div className="mb-4">
        <h2 className="mb-1"><FaHistory className="me-2 text-primary" />Employee Change History</h2>
        <p className="text-muted mb-0">Search Workday change history by email address. Nothing loads until a user is selected.</p>
      </div>

      <Form onSubmit={submit} className="mb-3">
        <Form.Group style={{ maxWidth: 560 }}>
          <Form.Label className="small text-muted mb-1">Employee Email</Form.Label>
          <InputGroup className="position-relative">
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              placeholder="Search employees from Workday change history"
              autoComplete="off"
            />
            {searching && <InputGroup.Text><Spinner size="sm" animation="border" /></InputGroup.Text>}
            <Button type="submit" disabled={!emailInput.trim()}>Load History</Button>
            {showSuggestions && suggestions.length > 0 && (
              <ul className="spa-suggestions spa-suggestions--input-group">
                {suggestions.map((s) => (
                  <li key={s.employeeEmail} onMouseDown={() => selectSuggestion(s)}>
                    <span className="fw-semibold">
                      {[s.employeeFirstName, s.employeeLastName].filter(Boolean).join(' ') || s.employeeEmail}
                    </span>
                    <span className="text-muted small ms-2">{s.employeeEmail}</span>
                    <span className="text-muted small ms-2">{s.changeCount} changes</span>
                    <span className="text-muted small ms-2">Last: {fmt(s.lastChangeDate)}</span>
                  </li>
                ))}
              </ul>
            )}
          </InputGroup>
        </Form.Group>
      </Form>

      {!selectedEmail && (
        <Alert variant="info">Enter or select an email address to load that employee's Workday change history.</Alert>
      )}

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {selectedEmail && loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : selectedEmail ? (
        <>
          <div className="mb-2 text-muted small">Showing history for <span className="fw-semibold">{selectedEmail}</span></div>
          <div className="table-responsive">
            <Table hover size="sm" className="spa-table align-middle">
              <thead>
                <tr>
                  <th><SortHeader label="Change" column="change" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Name" column="name" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Employee" column="employeeNum" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Position" column="position" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Department" column="department" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Type" column="type" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="End Date" column="endDate" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Manager" column="manager" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Loaded" column="loadDate" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((u) => (
                  <tr key={u.employeeHierarchyChangeId} className="spa-click-row" onClick={() => openUser(u.employeeEmail || u.employeeNum || u.employeeWindowsNtid)}>
                    <td>{u.change ? <Badge bg={u.change.toLowerCase().includes('term') ? 'danger' : 'secondary'}>{u.change}</Badge> : '-'}</td>
                    <td className="fw-semibold text-nowrap">
                      {[u.employeeFirstName, u.employeeLastName].filter(Boolean).join(' ') || '-'}
                      <FaExternalLinkAlt className="ms-2 text-muted small" />
                    </td>
                    <td>
                      <div>{u.employeeNum || '-'}</div>
                      {u.employeeWindowsNtid && <div className="text-muted small">{u.employeeWindowsNtid}</div>}
                    </td>
                    <td style={{ minWidth: 220 }}>{u.employeePositionTitle || '-'}</td>
                    <td>{u.employeeDeptId || '-'}</td>
                    <td>{u.employeeType ? <Badge bg="info">{u.employeeType}</Badge> : '-'}</td>
                    <td className="text-nowrap">{fmtDate(u.employeeEndDate)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 align-baseline"
                        onClick={(e) => { e.stopPropagation(); openUser(u.managerEmail || u.managerEmpId || u.managerWindowsNtid); }}
                        disabled={!u.managerEmail && !u.managerEmpId && !u.managerWindowsNtid}
                      >
                        <FaUserTie className="me-1" />{[u.managerFirstName, u.managerLastName].filter(Boolean).join(' ') || u.managerEmpId || '-'}
                      </button>
                      {u.managerEmail && <div className="text-muted small">{u.managerEmail}</div>}
                      {u.managerPositionTitle && <div className="text-muted small">{u.managerPositionTitle}</div>}
                    </td>
                    <td className="text-nowrap text-muted">{fmt(u.loadDate)}</td>
                  </tr>
                ))}
                {data?.items.length === 0 && (
                  <tr><td colSpan={9} className="text-center text-muted py-5">No change records found for this email</td></tr>
                )}
              </tbody>
            </Table>
          </div>
          {data && <PaginationBar page={page} pageSize={PAGE_SIZE} total={data.totalCount} onChange={setPage} />}
        </>
      ) : null}
    </Container>
  );
};

export default EmployeeHierarchyChangesPage;
